import { CalculatorItem } from '@/components/SpecialCalculators/CalculatorCategory';

export interface CalculatorCategory {
  title: string;
  description: string;
  calculators: CalculatorItem[];
}

export const calculatorCategories: CalculatorCategory[] = [
  {
    title: "Financial Calculators",
    description: "Calculate loans, mortgages, and financial planning",
    calculators: [
      {
        title: "Mortgage Calculator",
        description: "Calculate mortgage payments and amortization",
        icon: "🏠",
        path: "/special/mortgage",
        available: true
      },
      {
        title: "Auto Loan Calculator",
        description: "Calculate car payments and total cost",
        icon: "🚗",
        path: "/special/auto",
        available: true
      },
      {
        title: "Loan Calculator",
        description: "Calculate loan payments and interest",
        icon: "💰",
        path: "/special/loan",
        available: true
      },
      {
        title: "Investment Calculator",
        description: "Calculate returns on your investments",
        icon: "📈",
        path: "/special/investment",
        available: true
      },
      {
        title: "Retirement Calculator",
        description: "Plan for your retirement savings",
        icon: "👴",
        path: "/special/retirement",
        available: true
      },
      {
        title: "Tax Calculator",
        description: "Estimate income tax and take-home pay",
        icon: "📊",
        path: "/special/tax",
        available: true
      },
      {
        title: "Salary Calculator",
        description: "Convert between hourly and annual wages",
        icon: "💼",
        path: "/special/salary",
        available: true
      },
      {
        title: "Interest Calculator",
        description: "Calculate simple and compound interest",
        icon: "💲",
        path: "/special/interest",
        available: true
      },
      {
        title: "Debt Payoff Calculator",
        description: "Create a plan to become debt-free",
        icon: "📉",
        path: "/special/debt",
        available: true
      },
      {
        title: "Percentage Calculator",
        description: "Calculate percentages, increases, and decreases",
        icon: "%",
        path: "/special/percentage",
        available: true
      },
      {
        title: "Digital Ad Break-Even Calculator",
        description: "Determine break-even point for digital ad campaigns",
        icon: "📱",
        path: "/special/digital-ad-breakeven",
        available: true
      },
      {
        title: "Compound Interest Calculator",
        description: "Calculate compound interest with regular contributions",
        icon: "📈",
        path: "/special/compound-interest",
        available: true
      },
      {
        title: "ROI Calculator",
        description: "Calculate return on investment percentage",
        icon: "💹",
        path: "/special/roi",
        available: true
      },
      {
        title: "NPV Calculator",
        description: "Calculate net present value of investment",
        icon: "💰",
        path: "/special/npv",
        available: true
      },
      {
        title: "CAGR Calculator",
        description: "Calculate compound annual growth rate",
        icon: "📈",
        path: "/special/cagr",
        available: true
      },
      {
        title: "DTI Calculator", 
        description: "Calculate debt-to-income ratio",
        icon: "📊",
        path: "/special/dti",
        available: true
      },
      {
        title: "Discount Calculator",
        description: "Calculate discounted prices and savings",
        icon: "💰",
        path: "/special/discount",
        available: true
      },
      {
        title: "Emergency Fund Calculator",
        description: "Plan your emergency fund savings",
        icon: "🛡️",
        path: "/special/emergency-fund", 
        available: true
      },
      {
        title: "Net Worth Calculator",
        description: "Calculate your total net worth",
        icon: "💎",
        path: "/special/net-worth",
        available: true
      }
    ]
  },
  {
    title: "Health & Fitness Calculators",
    description: "Calculate BMI, calories, and other health metrics",
    calculators: [
      {
        title: "BMI Calculator",
        description: "Calculate your Body Mass Index",
        icon: "⚖️",
        path: "/special/bmi",
        available: true
      },
      {
        title: "Pregnancy Calculator",
        description: "Track your pregnancy progress week by week",
        icon: "👶",
        path: "/special/pregnancy",
        available: true
      },
      {
        title: "Pregnancy Weight Gain Calculator",
        description: "Calculate healthy pregnancy weight gain",
        icon: "⚖️",
        path: "/special/pregnancy-weight",
        available: true
      },
      {
        title: "Pregnancy Conception Calculator",
        description: "Calculate conception date based on due date",
        icon: "🗓️",
        path: "/special/pregnancy-conception",
        available: true
      },
      {
        title: "Due Date Calculator",
        description: "Estimate your baby's arrival date",
        icon: "📅",
        path: "/special/due-date",
        available: true
      },
      {
        title: "Ovulation Calculator",
        description: "Find your most fertile days",
        icon: "📆",
        path: "/special/ovulation",
        available: true
      },
      {
        title: "Conception Calculator",
        description: "Calculate potential conception dates",
        icon: "🗓️",
        path: "/special/conception",
        available: true
      },
      {
        title: "Period Calculator",
        description: "Track and predict your menstrual cycle",
        icon: "📊",
        path: "/special/period",
        available: true
      },
      {
        title: "Calorie Calculator",
        description: "Determine daily calorie needs",
        icon: "🍎",
        path: "/special/calories",
        available: true
      },
      {
        title: "Body Fat Calculator",
        description: "Estimate your body fat percentage",
        icon: "📏",
        path: "/special/bodyfat",
        available: true
      },
      {
        title: "BMR Calculator",
        description: "Calculate your Basal Metabolic Rate",
        icon: "🔥",
        path: "/special/bmr",
        available: true
      },
      {
        title: "Ideal Weight Calculator",
        description: "Find your ideal weight range",
        icon: "⚖️",
        path: "/special/ideal-weight",
        available: true
      },
      {
        title: "GFR Calculator",
        description: "Calculate Glomerular Filtration Rate for kidney function",
        icon: "🫘",
        path: "/special/gfr",
        available: true
      },
    ]
  },
  {
    title: "Time & Date Calculators",
    description: "Calculate ages, dates, and time differences",
    calculators: [
      {
        title: "Age Calculator",
        description: "Calculate exact age between two dates",
        icon: "📅",
        path: "/special/age",
        available: true
      },
      {
        title: "Date Calculator",
        description: "Calculate days between dates or add/subtract days",
        icon: "📆",
        path: "/special/date",
        available: false
      },
    ]
  },
  {
    title: "Statistical Calculators",
    description: "Analyze data and perform statistical tests",
    calculators: [
      {
        title: "ANOVA Calculator",
        description: "Analysis of Variance between multiple groups",
        icon: "📊",
        path: "/special/anova",
        available: true
      },
      {
        title: "Binomial Distribution Calculator",
        description: "Calculate probabilities for binomial distributions",
        icon: "🎲",
        path: "/special/binomial-distribution",
        available: true
      },
      {
        title: "Chi-Square Calculator",
        description: "Test association between categorical variables",
        icon: "χ²",
        path: "/special/chi-square",
        available: true
      },
    ]
  },
  {
    title: "Mathematical Calculators",
    description: "Solve complex mathematical functions",
    calculators: [
      {
        title: "Bessel Functions Calculator",
        description: "Calculate Bessel functions of the first and second kind",
        icon: "∫",
        path: "/special/bessel-functions",
        available: true
      },
    ]
  },
  {
    title: "Education Calculators",
    description: "Tools for students and educators",
    calculators: [
      {
        title: "GPA Calculator",
        description: "Calculate your Grade Point Average",
        icon: "🎓",
        path: "/special/gpa",
        available: true
      },
    ]
  },
  {
    title: "Everyday Calculators",
    description: "Helpful calculators for everyday situations",
    calculators: [
      {
        title: "Tip Calculator",
        description: "Calculate tips for restaurants and services",
        icon: "🍽️",
        path: "/special/tip",
        available: true
      },
    ]
  },
  {
    title: "Unit Converters",
    description: "Convert between different units of measurement",
    calculators: [
      {
        title: "Length Converter",
        description: "Convert between meters, feet, inches, etc.",
        icon: "📏",
        path: "/converters/length",
        available: true
      },
      {
        title: "Weight Converter",
        description: "Convert between kg, pounds, ounces, etc.",
        icon: "⚖️",
        path: "/converters/weight",
        available: true
      },
      {
        title: "Volume Converter",
        description: "Convert between liters, gallons, etc.",
        icon: "🥛",
        path: "/converters/volume",
        available: true
      },
      {
        title: "Temperature Converter",
        description: "Convert between Celsius, Fahrenheit, etc.",
        icon: "🌡️",
        path: "/converters/temperature",
        available: true
      },
      {
        title: "Area Converter",
        description: "Convert between square meters, acres, etc.",
        icon: "📐",
        path: "/converters/area",
        available: true
      },
    ]
  },
  {
    title: "Pregnancy Calculators",
    description: "Track pregnancy milestones and important dates",
    calculators: [
      {
        title: "Pregnancy Calculator",
        description: "Track your pregnancy progress week by week",
        icon: "👶",
        path: "/special/pregnancy",
        available: true
      },
      {
        title: "Pregnancy Weight Gain Calculator",
        description: "Calculate healthy pregnancy weight gain",
        icon: "⚖️",
        path: "/special/pregnancy-weight",
        available: true
      },
      {
        title: "Pregnancy Conception Calculator",
        description: "Calculate conception date based on due date",
        icon: "🗓️",
        path: "/special/pregnancy-conception",
        available: true
      },
      {
        title: "Due Date Calculator",
        description: "Estimate your baby's arrival date",
        icon: "📅",
        path: "/special/due-date",
        available: true
      },
      {
        title: "Ovulation Calculator",
        description: "Find your most fertile days",
        icon: "📆",
        path: "/special/ovulation",
        available: true
      },
      {
        title: "Conception Calculator",
        description: "Calculate potential conception dates",
        icon: "🗓️",
        path: "/special/conception",
        available: true
      },
      {
        title: "Period Calculator",
        description: "Track and predict your menstrual cycle",
        icon: "📊",
        path: "/special/period",
        available: true
      }
    ]
  }
];
